import { useEffect, useMemo, useState } from "react";
import "./WalletHelpCenter.css";

type HelpItem = {
  question:string;
  answer:string;
  keywords:string[];
};

const faq:HelpItem[] = [
  {
    question:"Life Passport는 무엇인가요?",
    answer:"학교 활동, 개인 경험, 검사, 목표, 성장 기록, Life Stamp를 한곳에 모아 나의 진로성장을 계속 기록하는 학생용 지갑입니다.",
    keywords:["passport","지갑","기록","진로"]
  },
  {
    question:"학생 활동배지는 공식 자격증명인가요?",
    answer:"아닙니다. 학생 활동배지는 내가 한 활동을 스스로 이름 붙여 남기는 비표준 기록입니다. 본사 인증과 공식 발급 절차를 통과한 경우에만 Open Badges 3.0 Life Stamp로 승격됩니다.",
    keywords:["활동배지","비표준","인증","open badges"]
  },
  {
    question:"본사 인증을 요청하면 바로 공식 배지가 되나요?",
    answer:"아닙니다. 연결된 활동·검증 증빙을 확인하고, 본사 관리자가 공식 배지 기준과 심사점수를 검토합니다. 기존 Achievement 기준을 충족해야 공식 Credential 발급 단계로 넘어갑니다.",
    keywords:["본사","인증","심사","공식"]
  },
  {
    question:"Life Stamp와 학생 활동배지의 차이는 무엇인가요?",
    answer:"Life Stamp는 발급기관과 성취 기준이 확인된 공식 자격증명입니다. 학생 활동배지는 학생이 자유롭게 만드는 활동 중심 자기기록으로, 본사 인증 전에는 공식 검증 링크나 Credential ID가 없습니다.",
    keywords:["life stamp","차이","credential","활동배지"]
  },
  {
    question:"기록을 다른 사람에게 보여주고 싶어요.",
    answer:"공유 메뉴에서 보여줄 기록만 선택하고, 이름·학교명 공개 범위와 유효기간을 정한 뒤 미리보기 후 공유 링크를 만들 수 있습니다.",
    keywords:["공유","링크","개인정보","미리보기"]
  },
  {
    question:"검사 결과는 자동으로 직업 추천이나 역량 판정에 사용되나요?",
    answer:"아닙니다. 자기이해 화면은 발행된 검사 결과와 학생 공개용 해석만 보여줍니다. 검사 결과만으로 직업·전공·역량·성취를 자동 확정하지 않습니다.",
    keywords:["검사","자기이해","직업추천","역량"]
  },
  {
    question:"보완 요청을 받았는데 어디서 확인하나요?",
    answer:"할 일 메뉴에서 우선 확인 항목을 보고 해당 경험 또는 증빙으로 바로 이동할 수 있습니다. 원본 기록을 실제로 수정·재제출해야 할 일이 사라집니다.",
    keywords:["보완","할 일","증빙","경험"]
  },
  {
    question:"기록으로 자기소개서나 포트폴리오를 만들 수 있나요?",
    answer:"만들기 메뉴에서 Life Stamp, 경험, 역량, 성취, 목표, 검사 결과를 선택해 문서 초안을 만들 수 있습니다.",
    keywords:["포트폴리오","자기소개서","문서","만들기"]
  },
  {
    question:"공유를 취소할 수 있나요?",
    answer:"가능합니다. 공유 메뉴의 내 공유 관리에서 활성 링크를 종료하면 이후 공개 링크를 사용할 수 없습니다.",
    keywords:["공유","취소","종료","링크"]
  },
  {
    question:"문제가 생기면 무엇을 확인해야 하나요?",
    answer:"먼저 새로고침과 재로그인을 확인하고, 계속 문제가 있으면 사용 중인 메뉴명, 발생 시각, 화면에 나온 오류 문구를 학교 또는 운영기관 담당자에게 전달하세요.",
    keywords:["오류","문의","로그인","문제"]
  }
];

const quickLinks = [
  ["/","홈","전체 성장 현황"],
  ["/inbox","할 일","지금 확인할 일과 알림"],
  ["/experiences","경험","학교·개인 활동 기록"],
  ["/activity-badges","활동배지","학생 자가발급 활동배지"],
  ["/assessments","검사","검사하기·자기이해"],
  ["/growth","성장","Growth Map·역량 이야기"],
  ["/goals","목표","목표·다음 행동"],
  ["/credentials","Life Stamp","공식 디지털배지"],
  ["/portfolio","만들기","포트폴리오·문서 생성"],
  ["/share","공유","공유 범위·개인정보 관리"]
] as const;

export function WalletHelpCenter({
  open,
  onClose,
  onNavigate
}:{
  open:boolean;
  onClose:()=>void;
  onNavigate:(path:string)=>void;
}){
  const [tab,setTab]=useState<"GUIDE"|"FAQ"|"BADGE"|"PRIVACY"|"TROUBLE">("GUIDE");
  const [query,setQuery]=useState("");

  useEffect(()=>{
    if(!open)return;
    const onKey=(event:KeyboardEvent)=>{
      if(event.key==="Escape")onClose();
    };
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  },[open,onClose]);

  const filteredFaq=useMemo(()=>{
    const needle=query.trim().toLowerCase();
    if(!needle)return faq;
    return faq.filter((item)=>
      [item.question,item.answer,...item.keywords]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  },[query]);

  if(!open)return null;

  function move(path:string){
    onNavigate(path);
    onClose();
  }

  return (
    <div className="wallet-help-layer" role="dialog" aria-modal="true" aria-label="Life Passport 도움말">
      <button className="wallet-help-backdrop" aria-label="도움말 닫기" onClick={onClose} />
      <aside className="wallet-help-panel">
        <header className="wallet-help-header">
          <div>
            <p className="eyebrow">LIFE PASSPORT HELP</p>
            <h2>사용안내 · Q&A</h2>
            <p>기록부터 활동배지, 공식 Life Stamp, 공유까지 빠르게 확인하세요.</p>
          </div>
          <button className="wallet-help-close" onClick={onClose} aria-label="닫기">×</button>
        </header>

        <div className="wallet-help-tabs">
          <button className={tab==="GUIDE"?"active":""} onClick={()=>setTab("GUIDE")}>사용안내</button>
          <button className={tab==="FAQ"?"active":""} onClick={()=>setTab("FAQ")}>Q&A</button>
          <button className={tab==="BADGE"?"active":""} onClick={()=>setTab("BADGE")}>배지·인증</button>
          <button className={tab==="PRIVACY"?"active":""} onClick={()=>setTab("PRIVACY")}>공유·개인정보</button>
          <button className={tab==="TROUBLE"?"active":""} onClick={()=>setTab("TROUBLE")}>문제해결</button>
        </div>

        <div className="wallet-help-body">
          {tab==="GUIDE"&&(
            <>
              <section className="wallet-help-intro">
                <strong>Life Passport 이용 흐름</strong>
                <div className="wallet-help-flow">
                  <span>1 · 기록</span><b>→</b><span>2 · 성장</span><b>→</b><span>3 · 배지</span><b>→</b><span>4 · 문서</span><b>→</b><span>5 · 공유</span>
                </div>
              </section>

              <section>
                <h3>빠른 이동</h3>
                <div className="wallet-help-link-grid">
                  {quickLinks.map(([path,label,description])=>(
                    <button key={path} onClick={()=>move(path)}>
                      <strong>{label}</strong>
                      <span>{description}</span>
                    </button>
                  ))}
                </div>
              </section>

              <section className="wallet-help-guide-cards">
                <article><span>01</span><strong>경험을 기록하세요</strong><p>학교 활동과 개인 경험을 남기고 필요한 증빙을 연결합니다.</p></article>
                <article><span>02</span><strong>성장을 확인하세요</strong><p>검사·역량·성취·목표를 한 흐름으로 확인합니다.</p></article>
                <article><span>03</span><strong>내 활동배지를 만드세요</strong><p>작은 활동도 직접 이름 붙여 학생 활동배지로 남길 수 있습니다.</p></article>
                <article><span>04</span><strong>공식 인증이 필요하면 요청하세요</strong><p>본사 심사를 통과한 경우에만 국제표준 Life Stamp로 승격됩니다.</p></article>
                <article><span>05</span><strong>필요한 기록만 공유하세요</strong><p>공유 범위·이름·학교명·유효기간을 직접 정할 수 있습니다.</p></article>
              </section>
            </>
          )}

          {tab==="FAQ"&&(
            <section>
              <label className="wallet-help-search">
                <span>⌕</span>
                <input value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="궁금한 내용을 검색하세요" />
              </label>
              <div className="wallet-help-faq">
                {filteredFaq.map((item)=>(
                  <details key={item.question}>
                    <summary>{item.question}</summary>
                    <p>{item.answer}</p>
                  </details>
                ))}
                {!filteredFaq.length&&<p className="wallet-help-empty">검색 결과가 없습니다.</p>}
              </div>
            </section>
          )}

          {tab==="BADGE"&&(
            <section className="wallet-help-badge-guide">
              <div className="wallet-help-tier-card student">
                <span>STUDENT</span>
                <h3>학생 활동배지 · 비표준</h3>
                <p>학생이 자신의 활동을 직접 기록하고 자가발급합니다.</p>
                <ul>
                  <li>자유롭게 이름·설명·아이콘·테마 설정</li>
                  <li>학교활동·개인경험·증빙 연결 가능</li>
                  <li>Credential ID 없음</li>
                  <li>공식 Issuer 검증 링크 없음</li>
                  <li>Open Badges 3.0으로 표시하지 않음</li>
                </ul>
                <button className="secondary" onClick={()=>move("/activity-badges")}>활동배지 만들기</button>
              </div>

              <div className="wallet-help-upgrade-arrow">본사 인증 요청 ↓</div>

              <div className="wallet-help-tier-card official">
                <span>HQ CERTIFIED</span>
                <h3>공식 Life Stamp · Open Badges 3.0</h3>
                <p>본사가 검증 증빙과 공식 성취 기준을 확인한 뒤 기존 발급 엔진으로 발행합니다.</p>
                <ul>
                  <li>검증 완료 증빙 필요</li>
                  <li>공식 배지 기준점수 충족 필요</li>
                  <li>Achievement 기준 엔진 통과</li>
                  <li>공식 Credential ID·검증 URL 생성</li>
                  <li>발급기관·현재 유효상태 공개 검증 가능</li>
                </ul>
                <button className="secondary" onClick={()=>move("/credentials")}>Life Stamp 보기</button>
              </div>
            </section>
          )}

          {tab==="PRIVACY"&&(
            <section className="wallet-help-text-sections">
              <article>
                <h3>공유는 학생이 직접 통제합니다</h3>
                <p>공유할 경험·성장·Life Stamp를 선택하고 이름 표시 방식, 학교명 공개 여부, 링크 유효기간을 정할 수 있습니다.</p>
              </article>
              <article>
                <h3>공유 전 미리보기</h3>
                <p>실제 링크를 만들기 전에 상대방에게 보이는 내용을 먼저 확인할 수 있습니다.</p>
              </article>
              <article>
                <h3>공유하지 않는 정보</h3>
                <p>개인 증빙 파일 원문, 학생 성찰 원문, 교사용 사례관리 정보는 일반 Passport 공유에 포함하지 않습니다.</p>
              </article>
              <article>
                <h3>언제든 종료</h3>
                <p>내 공유 관리에서 활성 링크를 종료하면 이후 해당 공개 링크를 사용할 수 없습니다.</p>
              </article>
              <button className="primary wallet-help-wide-button" onClick={()=>move("/share")}>공유 설정 열기</button>
            </section>
          )}

          {tab==="TROUBLE"&&(
            <section className="wallet-help-text-sections">
              <article><h3>1. 메뉴가 보이지 않아요</h3><p>페이지를 새로고침한 뒤 다시 로그인해 보세요. 계속 보이지 않으면 현재 Google 계정이 학생 Wallet에 연결되어 있는지 확인이 필요합니다.</p></article>
              <article><h3>2. 기록이 안 보여요</h3><p>학교 기록은 학생 계정과 learner 연결이 완료되어야 보입니다. 일부 기록은 교사 확인이나 검증 상태에 따라 노출 시점이 달라질 수 있습니다.</p></article>
              <article><h3>3. 본사 인증 버튼이 비활성화돼요</h3><p>활동 또는 증빙을 최소 1개 연결해야 인증을 요청할 수 있습니다. 공식 승격 승인에는 검증 완료 증빙이 필요합니다.</p></article>
              <article><h3>4. 오류가 계속돼요</h3><p>학교 또는 운영기관 담당자에게 메뉴명, 발생 시각, 오류 문구, 재현 순서를 전달하면 확인이 빨라집니다. 비밀번호나 인증코드는 전달하지 마세요.</p></article>
              <div className="wallet-help-support-note">
                <strong>문의할 때 준비하면 좋은 정보</strong>
                <span>사용 메뉴 · 발생 시각 · 화면 오류 문구 · 사용한 기기/브라우저 · 재현 순서</span>
              </div>
            </section>
          )}
        </div>

        <footer className="wallet-help-footer">
          <span>RS LIFE PASSPORT · 학생 중심 성장기록 지갑</span>
          <button onClick={onClose}>닫기</button>
        </footer>
      </aside>
    </div>
  );
}
